import React, { useState } from "react";
import { useParams } from "react-router-dom";

import styled from "styled-components";

import Icon from "components/atoms/Icon";
import Alert from "components/atoms/alert/Alert";
import useLoading from "components/atoms/loading/useLoading";
import Text from "components/atoms/text/Text";
import useToast from "components/atoms/toast/useToast";

import FormInput from "components/molecules/FormInput";
import AbstractModalFooter from "components/page/modal/AbstractModalFooter";

import { useSpaceMemberUpdate } from "hooks/queries/useUser";

const Container = styled.div`
    width: 100%;
`;

const SectionContents = styled.div`
    padding: 20px 10px 0 10px;
`;

const ProfileCard = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    max-width: 480px;
`;

const AvatarContainer = styled.div`
    position: relative;

    width: 72px;
    height: 72px;
`;
const AvatarWrap = styled.div`
    position: relative;
    width: 72px;
    height: 72px;
    border-radius: 50%;
    background: var(--color-Background);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    border: 1px solid var(--color-Line);
`;

const AvatarImg = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const CameraBadge = styled.label`
    position: absolute;
    right: 0px;
    bottom: 0px;

    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: var(--color-White);
    border: 1px solid var(--color-Line);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 2;
`;

const HiddenFile = styled.input`
    display: none;
`;

const HelpText = styled(Text).attrs({
    variant: "body-sm-400",
    color: "var(--color-Grey1)",
})`
    margin-top: 18px;
`;

const SubTitleWrap = styled.div`
    display: flex;
    width: 120px;
    flex-shrink: 0;
`;

const ProfileNameText = styled(Text).attrs({
    variant: "body-md-500",
})`
    margin-bottom: 8px;
`;

function SpaceMemberProfileModal(props) {
    const { space_uuid } = useParams();

    const { setLoading } = useLoading();
    const { setToast } = useToast();

    const spaceMember = props.member;
    const [spaceMemberName, setSpaceMemberName] = useState(spaceMember.name);

    const spaceMemberUpdateMutation = useSpaceMemberUpdate();

    return (
        <Container>
            <SectionContents>
                <ProfileCard>
                    <AvatarContainer>
                        <AvatarWrap>
                            {spaceMember?.image && <AvatarImg src={spaceMember?.image} alt="profile_image" />}
                        </AvatarWrap>
                        <CameraBadge htmlFor="space-member-profile-image-input" title="이미지 업로드">
                            <Icon name="camera" size={"14"} />
                        </CameraBadge>
                        <HiddenFile
                            id="space-member-profile-image-input"
                            type="file"
                            accept={["image/png", "image/jpeg", "image/jpg"].join(",")}
                            onChange={async (e) => {
                                if (!e.target.files || e.target.files.length === 0) {
                                    return;
                                }
                                if (e.target.files.length > 1) {
                                    Alert("warn", "이미지 업로드", "이미지는 한 개만 업로드해주세요.");
                                    return;
                                }
                                if (e.target.files[0].size > 2 * 1024 * 1024) {
                                    Alert("warn", "이미지 업로드", "이미지는 2MB 이하로 업로드해주세요.");
                                    return;
                                }
                                if (
                                    e.target.files[0].type !== "image/png" &&
                                    e.target.files[0].type !== "image/jpeg" &&
                                    e.target.files[0].type !== "image/jpg"
                                ) {
                                    Alert("warn", "이미지 업로드", "이미지는 JPG 또는 PNG 형식으로 업로드해주세요.");
                                    return;
                                }
                                const file = e.target.files[0];
                                if (!file) return;

                                const formData = new FormData();
                                formData.append("image", file);
                                formData.append("file_type", "image");

                                try {
                                    setLoading(true);
                                    await spaceMemberUpdateMutation.mutateAsync({
                                        space_uuid: space_uuid,
                                        space_member_id: spaceMember.id,
                                        formData,
                                    });
                                    setToast("프로필 이미지가 변경되었습니다.", "info");
                                } catch (e) {
                                    Alert("warn", "에러", "프로필 이미지 변경에 실패했습니다.");
                                } finally {
                                    setLoading(false);
                                }
                            }}
                        />
                    </AvatarContainer>

                    <div style={{ flex: 1 }}>
                        <SubTitleWrap>
                            <ProfileNameText>이름</ProfileNameText>
                        </SubTitleWrap>
                        <FormInput
                            labelDp="none"
                            height={40}
                            placeholder="입력해주세요(필수)"
                            value={spaceMemberName}
                            onChange={(e) => {
                                setSpaceMemberName(e.target.value);
                            }}
                            onBlur={async (e) => {
                                if (spaceMemberName === spaceMember.name) {
                                    return;
                                }
                                if (spaceMemberName.length < 1) {
                                    Alert("warn", "이름", "이름을 입력해주세요.");
                                    setSpaceMemberName(spaceMember.name);

                                    return;
                                }
                                if (spaceMemberName.length > 255) {
                                    Alert("warn", "이름", "이름은 255자 이하로 입력해주세요.");
                                    setSpaceMemberName(spaceMember.name);
                                    return;
                                }
                                try {
                                    setLoading(true);
                                    await spaceMemberUpdateMutation.mutateAsync({
                                        space_uuid: space_uuid,
                                        space_member_id: spaceMember.id,
                                        formData: {
                                            name: spaceMemberName,
                                        },
                                    });
                                    setToast("프로필 이름이 변경되었습니다.", "info");
                                } catch (error) {
                                    Alert("warn", "에러", error.response.data.error || "서버에 오류가 발생하였습니다.");
                                } finally {
                                    setLoading(false);
                                }
                            }}
                        />
                    </div>
                </ProfileCard>
                <HelpText>이미지는 JPG·PNG 형식, 2MB 이하만 가능합니다.</HelpText>
            </SectionContents>
        </Container>
    );
}

export default SpaceMemberProfileModal;
